import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, of, switchMap, tap } from 'rxjs';
import {
  initFetchRadios,
  initFetchRadiosFailed,
  initFetchRadiosSuccessful,
  initUserCountryCode,
  initUserCountryCodeFailed,
  initUserCountryCodeSuccessful,
  loadSuggestedRadios,
  muteAudioVolume,
  pauseMedia,
  playMedia,
  resumeMedia,
  unmuteAudioVolume,
  updateAudioVolume,
  updateSuggestedRadios,
} from './player.actions';
import { GetRadiosService } from './services/get-radios.service';
import { LocationService } from './services/location.service';
import { MediaPlayerService } from './services/media-player/media-player.service';

@Injectable()
export class MediaPlayerEffects {
  playMediaEffect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(playMedia),
        tap(({ mediaContent }) => {
          this.mediaPlayerService.play(mediaContent);
        })
      ),
    { dispatch: false }
  );
  pauseMediaEffect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(pauseMedia),
        tap(({}) => {
          this.mediaPlayerService.pauseStop();
        })
      ),
    { dispatch: false }
  );
  resumeMediaEffect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(resumeMedia),
        tap(({}) => {
          this.mediaPlayerService.resume();
        })
      ),
    { dispatch: false }
  );
  updateAudioVolume$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateAudioVolume),
        tap(({ newAudioVolume }) => {
          this.mediaPlayerService.updateVolume(newAudioVolume);
        })
      ),
    { dispatch: false }
  );
  muteAudioVolume$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(muteAudioVolume),
        tap(({}) => {
          this.mediaPlayerService.muteVolume();
        })
      ),
    { dispatch: false }
  );
  unmuteAudioVolume$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(unmuteAudioVolume),
        tap(({}) => {
          this.mediaPlayerService.unmuteVolume();
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions, // this is an RxJS stream of all actions
    private mediaPlayerService: MediaPlayerService // we will need this service for API calls
  ) {}
}

@Injectable()
export class RadiosAPIEffects {
  initFetchRadios$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initFetchRadios),
      exhaustMap((action) =>
        this.getRadiosService.getRadios(action.countryCode).pipe(
          map((radios) => {
            var data = JSON.parse(JSON.stringify(radios));
            return initFetchRadiosSuccessful({ allRadios: data.radioStations });
          }),
          catchError((error) => {
            console.log('Error: ', error);
            return of(initFetchRadiosFailed());
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions, // this is an RxJS stream of all actions
    private getRadiosService: GetRadiosService
  ) {}
}

@Injectable()
export class UserEffects {
  initUserCountryCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initUserCountryCode),
      exhaustMap((action) =>
        this.locationService.getIpAddressDetails().pipe(
          switchMap((countryCode) =>
            of(
              initUserCountryCodeSuccessful({
                countryCode: countryCode,
              }),
              initFetchRadios({ countryCode: countryCode })
            )
          ),
          catchError((error) => {
            console.log('Error: ', error);
            return of(
              initUserCountryCodeFailed(),
              initFetchRadios({ countryCode: 'US' })
            );
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions, // this is an RxJS stream of all actions
    private locationService: LocationService
  ) {}
}

@Injectable()
export class SuggestedRadiosEffects {
  /**
   * Effect that loads suggested radios based on the currently playing radio's tags
   * The suggestions are radios with similar tags, sorted by vote count
   */
  loadSuggestedRadios$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSuggestedRadios),
      map(({ currentRadio, allRadios }) => {
        // Get the current radio's tags
        const currentTags = currentRadio.tags
          .split(',')
          .map((tag) => tag.trim().toLowerCase())
          .filter((tag) => tag.length > 0);

        if (currentTags.length === 0) {
          // If no tags, return radios from the same country sorted by votes
          const suggested = allRadios
            .filter((radio) => radio.country === currentRadio.country && radio.name !== currentRadio.name)
            .sort((a, b) => (b.votes || 0) - (a.votes || 0))
            .slice(0, 6);
          return updateSuggestedRadios({ suggestedRadios: suggested });
        }

        // Score each radio based on tag matches
        const scoredRadios = allRadios
          .filter((radio) => radio.name !== currentRadio.name)
          .map((radio) => {
            const radioTags = radio.tags
              .split(',')
              .map((tag) => tag.trim().toLowerCase())
              .filter((tag) => tag.length > 0);

            // Count matching tags
            const matchingTags = radioTags.filter((tag) =>
              currentTags.includes(tag)
            ).length;

            // Calculate score: matches (weighted) + votes boost
            const score =
              matchingTags * 100 +
              (radio.votes || 0) * 0.1 +
              (radio.promoted ? 50 : 0) +
              (radio.country === currentRadio.country ? 25 : 0);

            return { radio, score, matchingTags };
          })
          .filter((item) => item.matchingTags > 0 || item.radio.country === currentRadio.country)
          .sort((a, b) => b.score - a.score)
          .slice(0, 6)
          .map((item) => item.radio);

        return updateSuggestedRadios({ suggestedRadios: scoredRadios });
      })
    )
  );

  constructor(private actions$: Actions) {}
}

